/**
 * /my-coaching/business/nova/assets/check-in — the weekly check-in under
 * My assets › Grow: this week's check-in (or its done state), when the next
 * one opens, "Change day · Pause check-ins", and past check-ins.
 *
 * Only a coach with NOVA routines (a paid plan and the routines flag) has one;
 * anyone else goes back to NOVA Home. On her first visit without a saved time
 * zone, the browser's zone is saved, so her check-ins open on her own clock.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";

import { CheckInDone } from "~/components/nova/check-in/check-in-done";
import { CheckInScheduleLinks } from "~/components/nova/check-in/check-in-schedule-dialog";
import { PastCheckIns } from "~/components/nova/check-in/past-check-ins";
import { useSaveBrowserTimeZone } from "~/components/nova/check-in/use-save-browser-time-zone";
import {
  checkInDayAndTime,
  weekOf,
} from "~/components/nova/check-in/view-model";
import { WeeklyCheckInForm } from "~/components/nova/check-in/weekly-check-in-form";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { PageHeader } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import { setMyNovaRoutinePaused } from "~/lib/functions/nova-routines";
import { novaHomeQueryKeys } from "~/lib/queries/novaHomeQueryKeys";
import { weeklyCheckInQueryOptions } from "~/lib/queries/weeklyCheckInQuery";

export const Route = createFileRoute(
  "/_user/my-coaching/business/nova/assets/check-in",
)({
  staticData: { title: "Weekly check-in" },
  loader: async ({ context }) => {
    // An optimisation for SSR; the page reads the same query and shows its
    // own error and retry.
    await context.queryClient
      .ensureQueryData(weeklyCheckInQueryOptions())
      .catch(() => undefined);
  },
  component: WeeklyCheckInPage,
});

function WeeklyCheckInPage() {
  const checkIn = useQuery(weeklyCheckInQueryOptions());
  const view = checkIn.data?.available ? checkIn.data : null;
  useSaveBrowserTimeZone(view !== null && !view.routine.timeZoneSaved);

  if (checkIn.isPending) {
    return (
      <div className="max-w-4xl space-y-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }
  if (checkIn.isError) {
    return (
      <div className="max-w-4xl space-y-4" role="alert">
        <p className="text-sm text-red-700">
          Couldn&apos;t load your check-in.
        </p>
        <Button onClick={() => void checkIn.refetch()} size="sm">
          Try again
        </Button>
      </div>
    );
  }
  if (!view) return <Navigate replace to="/my-coaching/business/nova" />;

  const { routine, current } = view;
  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        description={
          current
            ? weekOf(current.dueAt, routine.timeZone)
            : "Once a week: your numbers, last week's move and one move from NOVA."
        }
        eyebrow="My assets · Grow"
        title="Weekly check-in"
      />

      <Card>
        {routine.status !== "on" ? (
          <PausedCheckIn />
        ) : current?.status === "open" ? (
          <div className="space-y-6">
            {current.welcomeBack && (
              <p className="text-base text-gray-900">
                Welcome back. Let&apos;s pick up where you left off.
              </p>
            )}
            <WeeklyCheckInForm checkIn={current} key={current.runId} />
          </div>
        ) : current?.status === "completed" ? (
          <CheckInDone
            checkIn={current}
            streak={view.streak}
            timeZone={routine.timeZone}
          />
        ) : (
          <p className="text-text-secondary text-sm">
            Your next check-in opens{" "}
            {checkInDayAndTime(routine.nextRunAt, routine.timeZone)}.
          </p>
        )}
      </Card>

      <div className="space-y-2">
        <CheckInScheduleLinks
          routine={{ kind: "weekly_check_in", ...routine }}
        />
        <p className="text-text-tertiary text-xs">
          NOVA reads your answers to suggest your moves.
        </p>
      </div>

      <PastCheckIns past={view.past} timeZone={routine.timeZone} />
    </div>
  );
}

function PausedCheckIn() {
  const queryClient = useQueryClient();
  const resume = useMutation({
    mutationFn: () =>
      setMyNovaRoutinePaused({
        data: { kind: "weekly_check_in", paused: false },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: novaHomeQueryKeys.routines }),
  });
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="font-heading text-lg font-medium text-gray-900">
          Check-ins are paused
        </h2>
        <p className="text-text-secondary text-sm">
          No check-in, email or reminder until you turn them back on.
        </p>
      </div>
      {resume.isError && (
        <p className="text-sm text-red-700" role="alert">
          Couldn&apos;t turn them back on. Try again.
        </p>
      )}
      <Button
        className="w-full sm:w-auto"
        loading={resume.isPending}
        onClick={() => resume.mutate()}
        variant="primary"
      >
        Resume check-ins
      </Button>
    </div>
  );
}
